<script lang="ts">
	import { Button, ButtonSize, ButtonType, TextArea } from '@repo/commons/components'
	import { validateEmptyString, validateForm } from '@repo/commons/utils'
	import { chatT } from '@repo/commons/i18n/chat'
	import type { FormValidationItem } from '@repo/commons/models'

	interface Props {
		onHandleFeedback: (feedback: 'positive' | 'negative', comment?: string) => void
	}

	let { onHandleFeedback }: Props = $props()

	let step = $state<'buttons' | 'comment'>('buttons')
	let actionStatus = $state<'positive' | 'negative' | undefined>()
	let comment = $state<string>('')

	let errors: Record<string, string> = $state({})
	let touchedFields: Record<string, boolean> = $state({})

	const validationSchema: Record<string, FormValidationItem> = $derived.by(() => {
		const baseSchema: Record<string, FormValidationItem> = {}

		if (step === 'comment') {
			baseSchema.comment = {
				formComponent: comment,
				validators: [validateEmptyString('comment')]
			}
		}

		return baseSchema
	})

	function handleActionClick(feedback: 'positive' | 'negative') {
		if (feedback === 'negative') {
			onHandleFeedback('negative')
			return
		}
		step = 'comment'
		actionStatus = feedback
	}

	function sendFeedback() {
		errors = validateForm(validationSchema, touchedFields, true)
		if (actionStatus) {
			if (!Object.keys(errors).length) {
				onHandleFeedback(actionStatus, comment)
				step = 'buttons'
			}
		}
	}

	$effect(() => {
		errors = validateForm(validationSchema, touchedFields, false)
	})
</script>

<div class="feedbackWrapper">
	{#if step === 'buttons'}
		<div class="text">
			{$chatT('Feedback.Label')}
		</div>
		<div class="sub">
			<div class="sub-title">{$chatT('Feedback.Title')}</div>
			<div class="text">{$chatT('Feedback.Subtitle')}</div>
		</div>
		<div class="actions">
			<Button
				buttonType={ButtonType.INVERSE_OUTLINE}
				buttonSize={ButtonSize.MEDIUM}
				iconProps={{ name: 'like' }}
				buttonText={$chatT('Button.Yes')}
				onclick={() => {
					// Handle positive feedback
					handleActionClick('positive')
				}}
			/>
			<Button
				buttonType={ButtonType.INVERSE_OUTLINE}
				buttonSize={ButtonSize.MEDIUM}
				iconProps={{ name: 'dislike' }}
				buttonText={$chatT('Button.No')}
				onclick={() => {
					// Handle negative feedback
					handleActionClick('negative')
				}}
			/>
		</div>
	{:else if step === 'comment'}
		<div class="sub-title">{$chatT('Feedback.Comment.Title')}</div>

		<div class="text base">{$chatT('Feedback.Comment.Subtitle')}</div>
		<div class="comment">
			<TextArea
				placeholder={$chatT('Feedback.Comment.Placeholder')}
				label={$chatT('Feedback.Comment.Label')}
				bind:value={comment}
				onblur={() => (touchedFields.comment = true)}
				error={errors.comment}
			></TextArea>
		</div>
		<div class="actions">
			<Button
				buttonType={ButtonType.BRAND}
				buttonSize={ButtonSize.MEDIUM}
				buttonText={$chatT('Feedback.Comment.Submit')}
				onclick={sendFeedback}
			/>
		</div>
	{/if}
</div>

<style lang="postcss">
	.feedbackWrapper {
		@apply flex flex-col items-start mb-6;
	}
	.text {
		@apply text-[#1e1e1e] text-base font-normal leading-6;
	}

	.text.base {
		@apply mt-1 mb-2;
	}

	.sub {
		@apply flex flex-col gap-1 py-4 mb-4;
	}

	.sub-title {
		@apply text-[#1e1e1e] text-[20px] font-semibold leading-7;
	}

	.actions {
		@apply flex justify-between gap-4 w-full;
	}

	.comment {
		@apply pt-6 mb-4 w-full;
	}

	.feedbackWrapper :global(.actions .button-class) {
		@apply flex-1 border-[#007acc];
	}

	:global(.actions .inverse-outline .button-text) {
		@apply text-[#007acc];
	}

	:global(.textarea) {
		@apply !bg-white !border !border-[#002c4d80] !border-solid !resize-none;
	}
</style>
